import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Car, Sparkles } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import SEO from '../components/SEO';
import { LOCAL_BUSINESS_SCHEMA } from '../lib/schema';
import { submitContact } from '../lib/api';
import { track } from '../lib/analytics';
import { useLang } from '../context/LanguageContext';

const Contact = () => {
  const { search } = useLocation();
  const { c, ui, lang } = useLang();
  const t = ui.contact;
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle_type: '',
    interest: '',
    message: '',
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const res = params.get('resultaat');
    if (res) {
      setForm(f => ({ ...f, message: t.resultTemplate(res) }));
    }
  }, [search, t]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      track('contact_submitted', { interest: form.interest || 'onbekend', vehicle_type: form.vehicle_type || 'onbekend', language: lang });
      setSent(true);
      setForm({ name: '', email: '', phone: '', vehicle_type: '', interest: '', message: '' });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      alert(t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#0a0a0b] text-white pt-32 pb-24">
      <SEO
        path="/contact"
        title="Contact — Bel of e-mail ons"
        description="Neem contact op met Shiny-Cleaning in Deventer. Telefonisch, per e-mail of WhatsApp. Wij helpen u graag persoonlijk verder."
        keywords="Shiny-Cleaning contact, autodetailing Deventer telefoon, e-mail info"
        jsonLd={[LOCAL_BUSINESS_SCHEMA]}
      />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <SectionLabel>{t.label}</SectionLabel>
        <h1 className="mt-5 text-5xl md:text-7xl font-medium tracking-tight leading-[1.02] max-w-4xl">{t.title}</h1>
        <p className="mt-6 text-white/60 text-lg max-w-2xl">
          {t.subtitle}
        </p>

        <div className="mt-16 grid lg:grid-cols-5 gap-10 lg:gap-16">
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-start gap-4 p-6 rounded-xl border border-white/10 bg-white/[0.02]">
              <span className="w-11 h-11 rounded-md bg-[#2f6bff]/15 text-[#2f6bff] flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </span>
              <div>
                <div className="text-white/50 text-xs tracking-wider uppercase">{t.phone}</div>
                <a href={c.site.phoneHref} className="mt-1 block text-white text-lg hover:text-[#2f6bff] transition-colors">
                  {c.site.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 rounded-xl border border-white/10 bg-white/[0.02]">
              <span className="w-11 h-11 rounded-md bg-[#2f6bff]/15 text-[#2f6bff] flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <div className="text-white/50 text-xs tracking-wider uppercase">{t.email}</div>
                <a href={`mailto:${c.site.email}`} className="mt-1 block text-white text-lg hover:text-[#2f6bff] transition-colors">
                  {c.site.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 rounded-xl border border-white/10 bg-white/[0.02]">
              <span className="w-11 h-11 rounded-md bg-[#2f6bff]/15 text-[#2f6bff] flex items-center justify-center">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <div className="text-white/50 text-xs tracking-wider uppercase">{t.address}</div>
                <div className="mt-1 text-white text-lg">{c.site.location}</div>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 rounded-xl border border-white/10 bg-white/[0.02]">
              <span className="w-11 h-11 rounded-md bg-[#2f6bff]/15 text-[#2f6bff] flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </span>
              <div>
                <div className="text-white/50 text-xs tracking-wider uppercase">{t.hours}</div>
                <div className="mt-1 text-white text-sm space-y-0.5">
                  {t.hoursLines.map(l => <div key={l}>{l}</div>)}
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 p-8 md:p-10 rounded-xl border border-white/10 bg-white/[0.02] space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-xs tracking-wider uppercase mb-2">{t.name}</label>
                <input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-[#2f6bff] transition-colors"
                />
              </div>
              <div>
                <label className="block text-white/60 text-xs tracking-wider uppercase mb-2">{t.phone}</label>
                <input
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-[#2f6bff] transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-white/60 text-xs tracking-wider uppercase mb-2">{t.email}</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                required
                className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-[#2f6bff] transition-colors"
              />
            </div>

            <div>
              <label className="block text-white/60 text-xs tracking-wider uppercase mb-3 flex items-center gap-2">
                <Car className="w-3.5 h-3.5" /> {t.vehicleType}
              </label>
              <div className="flex flex-wrap gap-2">
                {t.vehicleTypes.map(v => {
                  const active = form.vehicle_type === v.label;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setForm({ ...form, vehicle_type: active ? '' : v.label })}
                      className={`px-4 py-2.5 rounded-md text-sm border transition-colors ${
                        active
                          ? 'bg-[#2f6bff] border-[#2f6bff] text-white'
                          : 'border-white/15 text-white/70 hover:text-white hover:border-white/30'
                      }`}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-xs tracking-wider uppercase mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> {t.interest}
              </label>
              <div className="flex flex-wrap gap-2">
                {t.interests.map(v => {
                  const active = form.interest === v.label;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setForm({ ...form, interest: active ? '' : v.label })}
                      className={`px-4 py-2.5 rounded-md text-sm border transition-colors ${
                        active
                          ? 'bg-[#2f6bff] border-[#2f6bff] text-white'
                          : 'border-white/15 text-white/70 hover:text-white hover:border-white/30'
                      }`}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-xs tracking-wider uppercase mb-2">{t.message}</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                required
                placeholder={t.messagePh}
                className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-[#2f6bff] transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-7 py-4 rounded-md bg-[#2f6bff] hover:bg-[#1f56e8] disabled:opacity-60 text-white text-sm font-medium transition-colors"
            >
              {loading ? t.submitting : sent ? t.sent : t.submit}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
